import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import {
  canManageFacilityAccess,
  getAdminUser,
  getAdminUsers,
  isFacilityRoleChangeBlocked,
  mergeUnmanagedFacilityAssignments,
  replaceAdminUserFacilities,
  updateAdminUserRole,
} from '../../lib/services/adminUsers'
import { getFacilities } from '../../lib/services/facilities'

const ROLE_OPTIONS = ['editor', 'user'] as const

type FacilityAssignableRole = (typeof ROLE_OPTIONS)[number]

function formatUserLabel(user: { first_name: string | null; last_name: string | null; email: string; role: string }): string {
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
  return `${fullName || user.email} (${user.role})`
}

export function FacilityAccessPage() {
  const { viewerContext } = useAuth()
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRole, setSelectedRole] = useState<FacilityAssignableRole>('user')
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([])
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)
  const canManage = canManageFacilityAccess(viewerContext)
  const allowedFacilityIds = useMemo(() => viewerContext?.facility_ids ?? [], [viewerContext?.facility_ids])

  const usersQuery = useQuery({
    queryKey: ['facility-access-users'],
    queryFn: getAdminUsers,
    enabled: canManage,
  })

  const facilitiesQuery = useQuery({
    queryKey: ['facility-access-facilities'],
    queryFn: getFacilities,
    enabled: canManage,
  })

  const detailQuery = useQuery({
    queryKey: ['facility-access-user-detail', selectedUserId],
    queryFn: () => getAdminUser(selectedUserId),
    enabled: canManage && !!selectedUserId,
  })

  const users = (usersQuery.data || []).filter((user) => user.active && user.role !== 'superadmin' && user.role !== 'admin')
  const facilities = facilitiesQuery.data || []
  const manageableFacilities = facilities.filter((facility) => allowedFacilityIds.includes(facility.facility_id))

  useEffect(() => {
    if (!detailQuery.data) return
    const role = detailQuery.data.user.role
    setSelectedRole(role === 'editor' ? 'editor' : 'user')
    setSelectedFacilityIds(
      detailQuery.data.facility_assignments
        .map((assignment) => assignment.facility_id)
        .filter((facilityId) => allowedFacilityIds.includes(facilityId)),
    )
  }, [detailQuery.data, allowedFacilityIds])

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Facility access management is restricted to facility-admin users.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Facility Access Management</h1>
        <p className="mt-1 text-sm text-slate-600">Manage editor and user access within your assigned facilities.</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Select User</h2>
        {usersQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading users...</p>}
        {usersQuery.error && <p className="mt-2 text-sm text-red-600">{(usersQuery.error as Error).message}</p>}
        {!usersQuery.isLoading && !usersQuery.error && users.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No facility-managed users are available.</p>
        )}
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={selectedUserId}
            onChange={(event) => setSelectedUserId(event.target.value)}
            disabled={users.length === 0}
          >
            <option value="">Choose user</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {formatUserLabel(user)}
              </option>
            ))}
          </select>
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={selectedRole}
            disabled={!selectedUserId}
            onChange={(event) => setSelectedRole(event.target.value as FacilityAssignableRole)}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-2 text-xs text-slate-500">Role changes to admin or superadmin are blocked for this workflow.</p>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Managed Facility Scope</h2>
        {facilitiesQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading facilities...</p>}
        {facilitiesQuery.error && <p className="mt-2 text-sm text-red-600">{(facilitiesQuery.error as Error).message}</p>}
        {!facilitiesQuery.isLoading && !facilitiesQuery.error && manageableFacilities.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">You are not assigned to any manageable facilities.</p>
        )}
        <div className="mt-3 grid gap-2">
          {manageableFacilities.map((facility) => (
            <label
              key={facility.facility_id}
              className="flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <input
                type="checkbox"
                disabled={!selectedUserId}
                checked={selectedFacilityIds.includes(facility.facility_id)}
                onChange={() =>
                  setSelectedFacilityIds((current) =>
                    current.includes(facility.facility_id)
                      ? current.filter((id) => id !== facility.facility_id)
                      : [...current, facility.facility_id],
                  )
                }
              />
              <span>{facility.facility_name}</span>
            </label>
          ))}
        </div>
        <button
          className="btn-primary mt-3 text-sm"
          disabled={isActing || !selectedUserId}
          onClick={async () => {
            setActionError(null)
            setActionSuccess(null)
            setIsActing(true)
            try {
              const detail = detailQuery.data
              if (!detail) throw new Error('Select a user before saving changes')
              if (isFacilityRoleChangeBlocked(detail.user.role)) {
                throw new Error('Unsupported role change target for facility workflow')
              }
              const nextFacilityIds = mergeUnmanagedFacilityAssignments(
                detail.facility_assignments,
                allowedFacilityIds,
                selectedFacilityIds,
              )

              await updateAdminUserRole(selectedUserId, selectedRole)
              await replaceAdminUserFacilities(selectedUserId, nextFacilityIds)
              setActionSuccess('Facility access updated.')
              await Promise.all([usersQuery.refetch(), detailQuery.refetch()])
            } catch (err) {
              setActionError((err as Error).message)
            } finally {
              setIsActing(false)
            }
          }}
        >
          Save Access
        </button>
        {detailQuery.error && <p className="mt-2 text-sm text-red-600">{(detailQuery.error as Error).message}</p>}
        {actionError && <p className="mt-2 text-sm text-red-600">{actionError}</p>}
        {actionSuccess && <p className="mt-2 text-sm text-emerald-700">{actionSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Facility Details</h2>
        <div className="mt-3 grid gap-3">
          {manageableFacilities.map((facility) => (
            <div key={facility.facility_id} className="v11-dashboard-card p-3 text-sm">
              <p className="font-bold text-slate-900">{facility.facility_name}</p>
              <p className="text-slate-600">
                {[facility.address, facility.city, facility.state, facility.zip_code].filter(Boolean).join(', ') || 'No address set'}
              </p>
              <p className="text-slate-600">
                Contact: {facility.contact_name || 'N/A'} {facility.contact_email ? `| ${facility.contact_email}` : ''}{' '}
                {facility.contact_phone ? `| ${facility.contact_phone}` : ''}
              </p>
              <p className="text-slate-600">Employee count: {facility.employee_count ?? 'N/A'}</p>
              <p className="text-slate-600">Specialties: {facility.specialties.length > 0 ? facility.specialties.join(', ') : 'N/A'}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
